import { useEffect } from 'react';
import type { SeoMeta } from '@dma/types';

const META_TAG_IDS = {
  description: 'dma-seo-description',
  ogTitle: 'dma-seo-og-title',
  ogDescription: 'dma-seo-og-description',
  ogImage: 'dma-seo-og-image',
  ogType: 'dma-seo-og-type',
  robots: 'dma-seo-robots'
} as const;

const CANONICAL_LINK_ID = 'dma-seo-canonical';
const DEFAULT_TITLE = 'داریوش معدن آذر';

function upsertMetaTag(id: string, attribute: 'name' | 'property', key: string, content: string) {
  let tag = document.getElementById(id) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement('meta');
    tag.id = id;
    tag.setAttribute(attribute, key);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
}

function removeTag(id: string) {
  document.getElementById(id)?.remove();
}

function upsertCanonicalLink(href: string) {
  let link = document.getElementById(CANONICAL_LINK_ID) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement('link');
    link.id = CANONICAL_LINK_ID;
    link.rel = 'canonical';
    document.head.appendChild(link);
  }
  link.href = href;
}

export interface UseDocumentHeadOptions {
  /** Marks the current page as excluded from search indexing (e.g. /admin/*). */
  noIndex?: boolean;
}

/**
 * Applies page-level SEO metadata to `document.head` — title, meta
 * description, canonical link, and basic Open Graph tags. Deliberately
 * plain DOM manipulation rather than react-helmet-async or similar: this is
 * a client-only Vite SPA with no SSR step, so there is nothing a helmet
 * library would give us here that a dozen lines of `useEffect` don't already
 * cover, and pulling in a new dependency for that would violate this
 * project's "no unnecessary dependencies" rule.
 *
 * Canonical URLs are built from `window.location` (origin + pathname, no
 * query/hash) rather than a configured site URL — correct for a client-
 * rendered SPA, since a crawler executing this page has already resolved
 * the real origin. Build-time artifacts that need an absolute URL before any
 * browser is involved (sitemap.xml, robots.txt) instead read the
 * `VITE_SITE_URL` env var — see scripts/generate-seo-files.mjs.
 */
export function useDocumentHead(seo: SeoMeta | null | undefined, options: UseDocumentHeadOptions = {}): void {
  useEffect(() => {
    const title = seo?.title ? `${seo.title} | ${DEFAULT_TITLE}` : DEFAULT_TITLE;
    document.title = title;

    if (seo?.description) {
      upsertMetaTag(META_TAG_IDS.description, 'name', 'description', seo.description);
      upsertMetaTag(META_TAG_IDS.ogDescription, 'property', 'og:description', seo.description);
    } else {
      removeTag(META_TAG_IDS.description);
      removeTag(META_TAG_IDS.ogDescription);
    }

    upsertMetaTag(META_TAG_IDS.ogTitle, 'property', 'og:title', title);
    upsertMetaTag(META_TAG_IDS.ogType, 'property', 'og:type', 'website');

    if (seo?.ogImage) {
      const absoluteImage = seo.ogImage.startsWith('http')
        ? seo.ogImage
        : `${window.location.origin}${seo.ogImage}`;
      upsertMetaTag(META_TAG_IDS.ogImage, 'property', 'og:image', absoluteImage);
    } else {
      removeTag(META_TAG_IDS.ogImage);
    }

    const canonicalHref = seo?.canonicalUrl ?? `${window.location.origin}${window.location.pathname}`;
    upsertCanonicalLink(canonicalHref);

    if (options.noIndex) {
      upsertMetaTag(META_TAG_IDS.robots, 'name', 'robots', 'noindex, nofollow');
    } else {
      removeTag(META_TAG_IDS.robots);
    }
  }, [seo, options.noIndex]);
}
